import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAppLogo } from '../hooks/useAppLogo';
import { authAPI } from '../services/api';
import '../styles/AuthPages.css';

const APP_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

function RegisterPage({ onRegister }) {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(getSystemTheme);
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = APP_LOGO || appLogoUrl;
  const [username, setUsername] = useState('');
  
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'night' : 'day');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  const [firstname, setFirstname] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        registration_type: 'user',
        username,
        email,
        password,
        firstname: firstname || undefined,
        surname: surname || undefined
      };
      // User registration - users can connect to companies after registration via the user-company connection feature

      const response = await authAPI.register(registerData);
      
      if (onRegister) {
        onRegister(response.data.user);
      } else {
        navigate('/login');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      const errorDetails = err.response?.data?.details;
      
      if (errorDetails) {
        // Format validation errors for display
        const formattedErrors = Object.entries(errorDetails)
          .map(([field, messages]) => {
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
            const messageList = Array.isArray(messages) ? messages.join(', ') : messages;
            return `${fieldName}: ${messageList}`;
          })
          .join('; ');
        setError(`${errorMessage}: ${formattedErrors}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-page theme-${theme}`}>
      <div className="auth-container">
        <div className="auth-logo">
          {logoUrl && (
            <div className="auth-logo-shell">
              <div className="auth-logo-glow"></div>
              <img src={logoUrl} alt="Frame-Book logo" className="auth-logo-image" />
            </div>
          )}
          <h1>Frame-Book</h1>
          <p>Bike Passport & Service History</p>
        </div>

        <div className="auth-card">
          <h2>Create Account</h2>
          
          {error && <div className="error">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div style={{padding: '12px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '10px', marginBottom: '15px', fontSize: '14px', color: '#374151', display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
              <Icon name="info" color="info" darkMode={false} size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span><strong style={{color: '#2563eb'}}>Info:</strong> After registration, you can request connections to service shops for bike maintenance.</span>
            </div>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                required
                disabled={loading}
                placeholder="Your first name"
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label>Surname</label>
              <input
                type="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                required
                disabled={loading}
                placeholder="Your surname"
                autoComplete="family-name"
              />
            </div>

            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                placeholder="Choose a username"
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email"
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Choose a password"
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>

          <div className="auth-footer">
            <p>Already have an account? <Link to="/login">Login here</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;

